/**
 * Icon Container Component
 * 
 * Displays all performance parameter icons in organized rows with background.
 */

import React from 'react';
import { useMusicianConfig } from '../hooks/useMusicianConfig';
import { GroupIcon } from './GroupIcon';
import { CustomParameterIcon } from './CustomParameterIcon';
import { CUSTOM_PARAM_INTERFACES } from '../config/musicians';
import './IconContainer.css';

/**
 * Background image for icon container
 */
function IconsBackground() {
  const { musicianName } = useMusicianConfig();
  
  // Use drummer background for drummer/percussion
  const backgroundSrc = musicianName === 'Drummer' ? './icons_bg2.png' : './icons_bg1.png';
  
  return <img className="icons-background" src={backgroundSrc} alt="Background" />;
}

/**
 * Icon container with three rows of parameter icons
 * 
 * @param {object} props - Component props
 * @param {object} props.region - Region data containing parameter groups
 */
export function IconContainer({ region }) {
  const { musicianIcons } = useMusicianConfig();
  
  const isKeyboardRange = region.customInterface === CUSTOM_PARAM_INTERFACES.KEYBOARD_RANGE;

  // Generate group 1 icons (with custom parameter at start)
  const group1Icons = [
    // Conditionally add custom param icon if it's not the special keyboard range case
    !isKeyboardRange && (
      <CustomParameterIcon
        key="custom"
        state={region.specialEffects}
        customInterface={region.customInterface}
      />
    ),
    ...(region.group1 || []).map((isOn, index) => {
      const iconName = musicianIcons?.group1?.[index];
      return iconName ? (
        <GroupIcon key={`g1-${index}`} isOn={isOn} iconName={iconName} />
      ) : null;
    }),
  ].filter(Boolean);
  
  // Generate group 2 icons
  const group2Icons = (region.group2 || [])
    .map((isOn, index) => {
      const iconName = musicianIcons?.group2?.[index];
      return iconName ? (
        <GroupIcon key={`g2-${index}`} isOn={isOn} iconName={iconName} />
      ) : null;
    })
    .filter(Boolean);
  
  // Generate group 3 icons
  const group3Icons = (region.group3 || [])
    .map((isOn, index) => {
      const iconName = musicianIcons?.group3?.[index];
      return iconName ? (
        <GroupIcon key={`g3-${index}`} isOn={isOn} iconName={iconName} />
      ) : null;
    })
    .filter(Boolean);
  
  return (
    <div className={`icon-container ${isKeyboardRange ? 'icon-container--with-keyboard' : ''}`}>
      <IconsBackground />
      {isKeyboardRange && (
        <div className="keyboard-range-container">
          <CustomParameterIcon
            key="custom"
            state={region.specialEffects}
            customInterface={region.customInterface}
          />
        </div>
      )}
      <div className={`icon-rows ${isKeyboardRange ? 'icon-rows--with-keyboard' : 'icon-rows--without-keyboard'}`}>
        <div className="icon-row">{group1Icons}</div>
        <div className="icon-row">{group2Icons}</div>
        <div className="icon-row">{group3Icons}</div>
      </div>
    </div>
  );
}


